/**
 * SessionManager — mints a `session_id` (uuid) that rotates after 30 minutes of
 * inactivity. Persisted via @lib/storage so it survives a JS reload inside the
 * window. In-memory state keeps `getSessionId()` synchronous (needed at enqueue
 * time); persistence is opportunistic.
 */
import uuid from "react-native-uuid";
import { getItem, setItem } from "@lib/storage";

const SESSION_KEY = "wisetap_session";
const IDLE_MS = 30 * 60 * 1000;

let sessionId: string | null = null;
let lastActive = 0;

const persist = (): void => {
  setItem(SESSION_KEY, JSON.stringify({ sessionId, lastActive })).catch(
    () => {},
  );
};

const rotate = (): string => {
  sessionId = String(uuid.v4());
  lastActive = Date.now();
  persist();
  return sessionId;
};

/** Rehydrate a still-valid session from storage, or start a fresh one. */
export const hydrateSession = async (): Promise<string> => {
  try {
    const raw = await getItem(SESSION_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (
        parsed?.sessionId &&
        Date.now() - (parsed.lastActive || 0) < IDLE_MS
      ) {
        sessionId = parsed.sessionId;
        lastActive = parsed.lastActive;
        return sessionId as string;
      }
    }
  } catch {
    // fall through to a fresh session
  }
  return rotate();
};

/** Current session id, rotating if idle beyond the window. Synchronous. */
export const getSessionId = (): string => {
  const now = Date.now();
  if (!sessionId || now - lastActive >= IDLE_MS) return rotate();
  lastActive = now; // extend the window; persisted lazily via touchSession()
  return sessionId;
};

/** Mark activity + persist (call on app foreground / periodic flush). */
export const touchSession = (): void => {
  lastActive = Date.now();
  persist();
};

/** Force a new session (call on logout). */
export const resetSession = (): string => rotate();
